import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Modal, Button, Form, InputGroup, Col } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import AlertService from "../../../services/AlertService";
import AuthContext from "../../../auth/AuthContext";
import DocumentService from "../../../services/DocumentService";
import DocumentTypeService from "../../../services/DocumentTypeService";

const AddDocumentModal = ({
  show,
  handleClose,
  document,
  openParentDeleteDialog,
  documentTypeId: initialDocumentTypeId,
  onSaved,
}) => {
  const [documentName, setDocumentName] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [fileData, setFileData] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTags, setDocumentTags] = useState([]);
  const [noOfPages, setNoOfPages] = useState(0);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState(null);
  const [selectedTaggedUnderId, setSelectedTaggedUnderId] = useState(null);
  const submitControllerRef = useRef(null);

  const fileInputRef = useRef(null);
  const authContext = useContext(AuthContext);

  const isEdit = !!document?.id;

  useEffect(() => {
    if (show) {
      if (isEdit) {
        setDocumentName(document.documentName || "");
        setSelectedFileName(document.attachments?.[0]?.fileName || "");
        setFileData(null);
        setSelectedDocumentTypeId(document.documentTypeId ?? null);
        setSelectedTaggedUnderId(document.taggedUnder ?? null);
        setNoOfPages(document.noOfPages ?? 0);
      } else {
        setDocumentName("");
        setSelectedFileName("");
        setFileData(null);
        setSelectedDocumentTypeId(initialDocumentTypeId ?? null);
        setSelectedTaggedUnderId(null);
        setNoOfPages(0);
      }
      setErrors({});
    }
  }, [show, isEdit, document, initialDocumentTypeId]);

 const fetchDocumentTypes = useCallback(async (signal) => { 
    try {
      const res = await DocumentTypeService.GetDocumentTypes(
        authContext,
        signal
      );

      if (signal?.aborted) return; 

      if (res?.isSuccess && Array.isArray(res.data)) {
        setDocumentTypes(res.data);
      } else {
        setDocumentTypes([]);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      setDocumentTypes([]);
    }
  }, [authContext]);

const fetchDocumentTags = useCallback(async (signal) => {
    try {
      const res = await DocumentTypeService.GetDocumentTags(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (res?.isSuccess && Array.isArray(res.data)) {
        setDocumentTags(res.data);
      } else {
        setDocumentTags([]);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      setDocumentTags([]);
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController();

    if (show) {
      fetchDocumentTypes(controller.signal); 
      fetchDocumentTags(controller.signal); 
    }

    return () => controller.abort(); 
  }, [show, fetchDocumentTags, fetchDocumentTypes]);

  const resetForm = () => {
    setDocumentName("");
    setSelectedFileName("");
    setFileData(null);
    setSelectedDocumentTypeId(null);
    setSelectedTaggedUnderId(null);
    setNoOfPages(0);
    setErrors({});
  };

  const onClose = () => {
    resetForm();
    handleClose();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!documentName.trim()) newErrors.documentName = "Please enter document name";
    if (!selectedFileName && !isEdit) newErrors.selectedFile = "Please upload a file";
    if (!selectedDocumentTypeId) newErrors.documentTypeId = "Please select a document type";
    if (!selectedTaggedUnderId) newErrors.taggedUnder = "Please select a category";
    if (!noOfPages) newErrors.noOfPages = "Please provide no of pages.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
      setFileData(file);
    }
  };

const handleSubmit = async () => {
  if (!validateForm()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const formData = new FormData();

    if (fileData) {
      if (isEdit && document.attachments?.length > 0) {
        const oldFileId = document.attachments[0].id;
        const res = await DocumentService.RemoveDocument(
          oldFileId,
          authContext,
          controller.signal
        );

        if (controller.signal.aborted) return;

        if (!res.isSuccess) {
          AlertService.error("Old file delete failed");
          return;
        }
      }
      formData.append("files", fileData);
    }

    formData.append("documentName", documentName);
    formData.append("noOfPages", noOfPages);
    if (isEdit) formData.append("id", document.id);
    formData.append("documentTypeId", selectedDocumentTypeId);
    formData.append("taggedUnder", selectedTaggedUnderId);

    const res = await DocumentService.Save(
      formData,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (res.isSuccess) {
      AlertService.success(
        `Document ${isEdit ? "updated" : "uploaded"} successfully!`
      );
      onSaved && onSaved(res.data);
      resetForm();
      handleClose();
    } else {
      AlertService.error(res.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Something went wrong!");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  return (
    <Modal show={show} onHide={onClose} backdrop="static" centered size="xl">
      <Modal.Header closeButton className="border-0 pt-3">
        <Modal.Title>{isEdit ? "Edit Document" : "Add New Document"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Document Name</Form.Label>
            <Form.Control
              type="text"
              className="custom-input custom-input-color"
              placeholder="Enter Document Name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
            />
            {errors.documentName && <div className="text-danger">{errors.documentName}</div>}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Total No. of Pages</Form.Label>
            <Form.Control
              type="number"
              className="custom-input custom-input-color"
              placeholder="Total No. of Pages"
              value={noOfPages}
              onChange={(e) => setNoOfPages(e.target.value)}
            />
            {errors.noOfPages && <div className="text-danger">{errors.noOfPages}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Document Folder</Form.Label>
            <Form.Select
              className="custom-input custom-input-color"
              value={selectedDocumentTypeId ?? ""}
              onChange={(e) =>
                setSelectedDocumentTypeId(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">Select Document Folder</option>
              {documentTypes.map((dt) => (
                <option key={dt.value} value={dt.value}>
                  {dt.label}
                </option>
              ))}
            </Form.Select>
            {errors.documentTypeId && <div className="text-danger">{errors.documentTypeId}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Under</Form.Label>
            <Form.Select
              className="custom-input custom-input-color"
              value={selectedTaggedUnderId ?? ""}
              onChange={(e) =>
                setSelectedTaggedUnderId(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">Select Document Category</option>
              {documentTags.map((tag) => (
                <option key={tag.value} value={tag.value}>
                  {tag.label}
                </option>
              ))}
            </Form.Select>
            {errors.taggedUnder && <div className="text-danger">{errors.taggedUnder}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Upload File</Form.Label>
            <Col>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Upload file"
                  readOnly
                  value={selectedFileName}
                  className="py-3 custom-input-color"
                />
                <Button variant="primary" className="bgColor px-3" onClick={() => fileInputRef.current.click()}>
                  <FiUpload className="fs-4" />
                </Button>
              </InputGroup>
              <Form.Control
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="d-none"
              />
              {errors.selectedFile && <div className="text-danger">{errors.selectedFile}</div>}
            </Col>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 pb-4 px-4">
        <div className="d-flex justify-content-between w-100 flex-wrap gap-3">
          {isEdit && (
            <div
              className="text-danger d-flex align-items-center"
              onClick={() => {
                handleClose();
                if (openParentDeleteDialog) openParentDeleteDialog(document);
              }}
              style={{ fontWeight: 500, cursor: "pointer" }}
            >
              <FaTrash className="me-2" size={20} />
              Delete
            </div>
          )}

          <div className="ms-auto d-flex gap-3">
            <Button variant="outline-secondary" className="px-5 py-2" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" className="px-5 py-2" onClick={handleSubmit} disabled={loading}>
              {isEdit ? "Update" : "Submit"}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddDocumentModal;
